import { uniqBy } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DGT_Pagination } from "../utils/utils";

interface UseInfiniteScrollParams<T> {
  /** Pagination state owned by the caller and fed into its paginated query. */
  pagination: DGT_Pagination;
  /** Setter for that pagination state — the hook advances it on scroll. */
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  /** Current page's items, straight from the paginated query. */
  content?: T[];
  /** Total number of records across all pages (from `page.totalElements`). */
  totalElements?: number;
  /** Whether the query is currently fetching — blocks load-more while in flight. */
  isFetching?: boolean;
  /** Property used to de-dupe accumulated items across pages. */
  dedupeKey?: keyof T | string;
  /** Distance from the bottom (px) at which the next page is requested. */
  thresholdPx?: number;
}

interface UseInfiniteScrollReturn<T> {
  /** Accumulated items across every loaded page. */
  items: T[];
  /** True once every available record has been loaded. */
  atEnd: boolean;
  /** Reset to page 0 and clear the list (e.g. on filter/date-range change). */
  reset: () => void;
  /** Attach to a scroll container's `onScroll` — call with the scrolled element. */
  onScroll: (containerRefElement?: HTMLElement | null) => void;
}

interface ScrollMetrics {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
  thresholdPx: number;
}

/** True once the container is scrolled within `thresholdPx` of its bottom. */
export const isNearScrollEnd = ({
  scrollHeight,
  scrollTop,
  clientHeight,
  thresholdPx,
}: ScrollMetrics): boolean =>
  scrollHeight - scrollTop - clientHeight <= thresholdPx;

interface AdvanceDecision {
  isFetching: boolean;
  atEnd: boolean;
  pending: boolean;
  nearEnd: boolean;
}

/** Whether a scroll event should request the next page. */
export const shouldAdvancePage = ({
  isFetching,
  atEnd,
  pending,
  nearEnd,
}: AdvanceDecision): boolean => !isFetching && !atEnd && !pending && nearEnd;

/**
 * Generic accumulate-on-scroll infinite scroll for Spring-style paginated
 * queries. The caller owns `pagination` (so it can pass it to its query) and
 * hands it to this hook along with the resulting page `content`/`totalElements`;
 * the hook accumulates pages, detects the scroll threshold, and advances the
 * page via `setPagination`. Page 0 replaces the list (fresh load / reset);
 * later pages are appended and de-duped by `dedupeKey`.
 *
 * @author [Rajkumar Rathod]
 */
export const useInfiniteScroll = <T>({
  pagination,
  setPagination,
  content,
  totalElements = 0,
  isFetching = false,
  dedupeKey = "uuid",
  thresholdPx = 13,
}: UseInfiniteScrollParams<T>): UseInfiniteScrollReturn<T> => {
  const [items, setItems] = useState<T[]>([]);
  // A scroll is a continuous-priority event, so `isFetching` lags a page
  // request by a render; this synchronous lock stops a second scroll event
  // from advancing the page again and skipping a page.
  const pendingRef = useRef(false);

  // Every available record is loaded once the pages fetched so far cover the
  // total. Derived (not scroll-triggered) so a list shorter than its container
  // — which can never fire a scroll event — still reports the end correctly.
  const atEnd =
    !isFetching && (pagination.page + 1) * pagination.pageSize >= totalElements;

  useEffect(() => {
    const page = content ?? [];
    // Page 0 is a fresh list (initial load / reset); later pages are appended
    // and de-duped so a shifted last page can't render an item twice.
    setItems((prev) =>
      pagination.page === 0
        ? page
        : uniqBy([...prev, ...page], dedupeKey as string)
    );
    pendingRef.current = false;
    // Intentionally keyed on `content` only — `pagination.page` is already
    // current by the time a new page's content arrives.
  }, [content]);

  // Release the lock once a request settles even if its content never changed
  // (e.g. a failed or empty page), so scroll can't stay stuck.
  useEffect(() => {
    if (!isFetching) pendingRef.current = false;
  }, [isFetching]);

  const reset = useCallback(() => {
    pendingRef.current = false;
    setPagination((prev) => ({ ...prev, page: 0 }));
    setItems([]);
  }, [setPagination]);

  const onScroll = useCallback(
    (containerRefElement?: HTMLElement | null) => {
      if (!containerRefElement) return;
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const nearEnd = isNearScrollEnd({
        scrollHeight,
        scrollTop,
        clientHeight,
        thresholdPx,
      });
      if (
        !shouldAdvancePage({
          isFetching,
          atEnd,
          pending: pendingRef.current,
          nearEnd,
        })
      )
        return;

      pendingRef.current = true;
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    },
    [isFetching, atEnd, thresholdPx, setPagination]
  );

  return { items, atEnd, reset, onScroll };
};
